import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Container, Button } from "react-bootstrap";
import { useParams, useHistory, useLocation } from "react-router-dom";
import StartupHeader from "../../../Components/StartupHeader";
import VideoPlayer from "./VideoPlayer";
import PostVideoModal from "./PostVideoModal";
import MemberConnectionModal from "./MemberConnectionModal";
import EmailTestimonials from "./EmailTestimonials";
import { logDemoViewed } from "../../../Services/MarketingContacts";
import { getDemoViewSource } from "../../../utils/demoViewSource";

const INTRO_ONE_URL =
  "https://elqmq.upcloudobjects.com/demo-videos/mod/intro01.mp4";
const INTRO_TWO_URL =
  "https://elqmq.upcloudobjects.com/demo-videos/mod/intro02.mp4";

// Section indices for the v2 flow
const SECTION_INTRO_ONE = 0;
const SECTION_EMAILS = 1;
const SECTION_INTRO_TWO = 2;
const SECTION_DEMO = 3;

// The intro (intro01 → emails → intro02) plays full-width with no chapter
// list; chapters only appear once the viewer reaches the demo video.
const PLACEHOLDER = { timeStampDetails: [{ startTime: 0, endTime: 0 }] };

const demoVideoParts = [
  { categoryTitle: "Introduction", ...PLACEHOLDER },
  {
    categoryTitle: "Job Seeker Sends A Networking Request",
    categoryDescription: "",
    videoUrl:
      "https://elqmq.upcloudobjects.com/demo-videos/mod/v2-sample-group.mp4",
    videoStyle: "portrait",
    timeStampDetails: [
      {
        title: "Login as a Job Seeker",
        startTime: -1,
        endTime: 32,
        description: "",
        slideText: `**Employed {{MEMBER_NOUN}}s:**\n Employed Individuals who are {{member_noun}}s of your Organization\n\n**{{JOBSEEKER_NAME}}:**\nSample {{INDUSTRY}} Job Seeker`,
      },
      {
        title: "Navigate to the Networking Page",
        startTime: 32,
        endTime: 49,
        description: "",
        slideText: `{{JOBSEEKER_NAME_FIRST}} Networks`,
      },
      {
        title: "Filter by Industry and Group",
        startTime: 49,
        endTime: 60 + 20,
        description: "",
        slideText: `{{JOBSEEKER_NAME_FIRST}} Filters by Industry and Group`,
      },
      {
        title: "Select an employed {{MEMBER_NOUN}} to network with",
        startTime: 60 + 20,
        endTime: 60 * 2 + 19,
        description: "",
        slideText:
          "{{JOBSEEKER_NAME_FIRST}} identifies ideal network candidates",
      },
      {
        title: "Send a Saved Resume with the Networking Request",
        startTime: 60 * 2 + 19,
        endTime: 60 * 2 + 39,
        description: "",
        slideText:
          "Job Seeker {{JOBSEEKER_NAME_FIRST}} sends resume & request to network to chosen Employed {{MEMBER_NOUN}}s",
      },
    ],
  },
  {
    categoryTitle: "Employed {{MEMBER_NOUN}} Receives Networking Request",
    categoryDescription: "",
    videoUrl:
      "https://elqmq.upcloudobjects.com/demo-videos/mod/v2-sample-group.mp4",
    videoStyle: "portrait",
    timeStampDetails: [
      {
        title: "Employed {{MEMBER_NOUN}} logs in to account",
        startTime: 60 * 2 + 39,
        endTime: 60 * 3,
        description: "",
        slideText:
          "Employed {{MEMBER_NOUN}} & {{STM_POSITION}}, **{{STM_NAME}}**\n logs in",
      },
      {
        title: "Networking Request Notification",
        startTime: 60 * 3,
        endTime: 60 * 3 + 19,
        description: "",
        slideText:
          "Employed {{MEMBER_NOUN}} {{STM_NAME_FIRST}} received notice of networking request in Messages and in {{JOBSEEKER_PRONOUN_POSSESSIVE}} email",
      },
      {
        title: "View Networking Request in Messages",
        startTime: 60 * 3 + 19,
        endTime: 60 * 3 + 30,
        description: "",
        slideText:
          "Employed {{MEMBER_NOUN}} {{STM_NAME_FIRST}} received notice of networking request in Messages and in {{JOBSEEKER_PRONOUN_POSSESSIVE}} email",
      },
      {
        title: "Navigate to the Connections Page",
        startTime: 60 * 3 + 30,
        endTime: 60 * 3 + 38,
        description: "",
        slideText: "{{STM_NAME_FIRST}} goes to **Connections**",
      },
      {
        title: "Reviews Job Seeker's resume",
        startTime: 60 * 3 + 38,
        endTime: 60 * 4 + 8,
        description: "",
        slideText: `Employed {{MEMBER_NOUN}} {{STM_NAME_FIRST}} reviews {{JOBSEEKER_NAME_FIRST}}'s resume, thinking of ways to help`,
      },
      {
        title: "Review 3 response options",
        startTime: 60 * 4 + 8,
        endTime: 60 * 5 + 2,
        description: "",
        slideText:
          "Employed {{MEMBER_NOUN}} selects 1 of 3 potential responses",
      },
      {
        title: "Respond to the Job Seeker",
        startTime: 60 * 5 + 2,
        endTime: 60 * 5 + 36,
        description: "",
        slideText:
          "Employed {{MEMBER_NOUN}} {{STM_NAME_FIRST}} chooses the 1st response and provides contact information",
      },
    ],
  },
  {
    categoryTitle: "Job Seeker Receives Response",
    categoryDescription: "",
    videoUrl:
      "https://elqmq.upcloudobjects.com/demo-videos/mod/v2-sample-group.mp4",
    videoStyle: "portrait",
    timeStampDetails: [
      {
        title: "Job Seeker logs in to account ",
        startTime: 60 * 5 + 36,
        endTime: 60 * 6,
        description: "",
        slideText:
          "Job Seeker received notice in their account **and** in email that the {{STM_POSITION}} at {{STM_COMPANY}} requested contact.",
      },
      {
        title: "Navigate to the Connections Page",
        startTime: 60 * 6,
        endTime: 60 * 6 + 23,
        description: "",
        slideText: `Job Seeker {{JOBSEEKER_NAME_FIRST}} goes to **Connections**`,
      },
      {
        title: "View the Employed {{MEMBER_NOUN}}'s response",
        startTime: 60 * 6 + 23,
        endTime: 60 * 6 + 59,
        description: "",
        slideText:
          "{{JOBSEEKER_NAME_FIRST}} clicks on the Response and the full message is displayed",
      },
      {
        title: "Summary of Networking Example",
        startTime: 60 * 6 + 59,
        endTime: 60 * 8,
        description: "",
        slideText:
          "There is Real Untapped Potential:\n\n**a.** Employed {{MEMBER_NOUN}}s are motivated to assist job seekers within their Church, Charitable Organization, etc.. \n\n**b.** Your job seekers can network in their industry with those who are motivated to help.",
      },
    ],
  },
];

export default function DynamicVideoPlayerV2() {
  const { videoId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // `?skipIntro=1` jumps straight to the demo video, which opens on the
  // "Job Seeker Sends A Networking Request" section.
  const skipIntro = ["1", "true"].includes(
    new URLSearchParams(location.search).get("skipIntro")
  );
  const [activeSection, setActiveSection] = useState(
    skipIntro ? SECTION_DEMO : SECTION_INTRO_ONE
  );
  const [videoMetadata, setVideoMetadata] = useState(null);
  const [processedDemoParts, setProcessedDemoParts] = useState(demoVideoParts);
  const [showPostVideoModal, setShowPostVideoModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);

  // Function to get first name from full name
  const getFirstName = (fullName) => {
    if (!fullName) return "";
    return fullName.split(" ")[0];
  };

  // Function to replace placeholders in text
  const replacePlaceholders = (text, memberNoun) => {
    if (!text || !memberNoun) return text;

    const titleCaseMemberNoun =
      memberNoun.charAt(0).toUpperCase() + memberNoun.slice(1);

    return text
      .replace(/\{\{member_noun\}\}/g, memberNoun)
      .replace(/\{\{MEMBER_NOUN\}\}/g, titleCaseMemberNoun);
  };

  // Function to process slideText with metadata
  const processSlideText = (slideText, metadata) => {
    if (!slideText || !metadata) return slideText;

    // Get first names
    const stm_name_first = getFirstName(metadata.stm_name);
    const jobseeker_name_first = getFirstName(metadata.jobseeker_name);

    // Replace all possible metadata placeholders
    let processedText = slideText;

    // Replace member_noun placeholders first
    if (metadata.member_noun) {
      processedText = replacePlaceholders(processedText, metadata.member_noun);
    }

    // Replace additional metadata placeholders
    const replacements = {
      "{{STM_NAME}}": metadata.stm_name || "",
      "{{STM_NAME_FIRST}}": stm_name_first,
      "{{STM_POSITION}}": metadata.stm_position_name || "",
      "{{STM_COMPANY}}": metadata.stm_company_name || "",
      "{{JOBSEEKER_NAME}}": metadata.jobseeker_name || "",
      "{{JOBSEEKER_NAME_FIRST}}": jobseeker_name_first,
      "{{RESUME_TITLE}}": metadata.resume_title || "",
      "{{INDUSTRY}}": metadata.industry || "",
      "{{GROUP_NAME}}": metadata.group_name || "",
      "{{JOBSEEKER_PRONOUN_POSSESSIVE}}":
        metadata.jobseeker_pronoun_possessive || "",
      "{{JOBSEEKER_PRONOUN}}": metadata.jobseeker_pronoun || "",
    };

    Object.entries(replacements).forEach(([placeholder, value]) => {
      processedText = processedText.replace(
        new RegExp(placeholder, "g"),
        value
      );
    });

    return processedText;
  };

  // Function to process video parts and replace placeholders
  const processVideoCategories = (categories, metadata) => {
    if (!metadata) return categories;

    const memberNoun = metadata.member_noun
      ? metadata.member_noun.toLowerCase()
      : "";

    return categories.map((category) => {
      // Process category title
      const categoryTitle = replacePlaceholders(
        category.categoryTitle,
        memberNoun
      );

      // Process timestamps and their content
      const timeStampDetails = category.timeStampDetails.map((detail) => {
        return {
          ...detail,
          title: replacePlaceholders(detail.title, memberNoun),
          description: replacePlaceholders(detail.description, memberNoun),
          slideText: processSlideText(detail.slideText, metadata),
        };
      });

      return {
        ...category,
        categoryTitle,
        timeStampDetails,
      };
    });
  };

  // Process video parts when metadata changes
  useEffect(() => {
    if (videoMetadata) {
      setProcessedDemoParts(
        processVideoCategories(demoVideoParts, videoMetadata)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoMetadata]);

  useEffect(() => {
    document.body.classList.add("home-page");

    return function cleanup() {
      document.body.classList.remove("home-page");
    };
  }, []);

  // Log demo view in the background when videoId is available. This landing
  // call owns the view count and last-viewed stamp. A ?skipIntro=1 link lands
  // straight on the demo video, so label the alert with wherever they started.
  useEffect(() => {
    if (!videoId) return;
    const source = getDemoViewSource(location.search);
    logDemoViewed(videoId, source, skipIntro ? "demo" : "intro").catch((err) => {
      console.warn("Failed to log demo view:", err);
    });
  }, [videoId, location.search, skipIntro]);

  // Alert the team as soon as the viewer starts the main demo video, past the
  // intro. Notify-only: the landing call above already counted this view. Fires
  // at most once per page view even if they navigate back to the intro and
  // return, and not at all when they landed on the demo to begin with.
  const demoSectionLogged = useRef(skipIntro);
  useEffect(() => {
    if (!videoId || activeSection !== SECTION_DEMO) return;
    if (demoSectionLogged.current) return;
    demoSectionLogged.current = true;
    const source = getDemoViewSource(location.search);
    logDemoViewed(videoId, source, "demo", true).catch((err) => {
      console.warn("Failed to log demo video start:", err);
    });
  }, [videoId, activeSection, location.search]);

  // Fire GA event once metadata (group_name) is loaded
  useEffect(() => {
    if (!videoId || !videoMetadata) return;
    if (typeof window.gtag !== "function") return;
    const source = getDemoViewSource(location.search);
    window.gtag("event", "demo_viewed", {
      demo_id: videoId,
      source,
      group_name: videoMetadata.group_name || "",
    });
    if (source !== "admin") {
      window.gtag("event", "conversion", {
        send_to: "G-6NZP2R5CJG/demo_viewed",
        demo_id: videoId,
        group_name: videoMetadata.group_name || "",
      });
      window.gtag(
        "event",
        `demo-${videoMetadata.group_name.replace(/\s+/g, "_").toLowerCase() || "unknown"}`,
        {
          demo_id: videoId,
          source,
          group_name: videoMetadata.group_name || "",
        }
      );
    }
  }, [videoId, videoMetadata, location.search]);

  // Fetch the video metadata when the component mounts or the videoId changes
  useEffect(() => {
    if (!videoId) {
      setError("No video ID provided");
      setLoading(false);
      return;
    }

    const fetchVideoData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://elqmq.upcloudobjects.com/demo-videos/mod/${videoId}_metadata.json`
        );

        if (!response.ok) {
          // If the response is a 404 (not found), redirect to videodemosample
          if (response.status === 404) {
            console.error(
              "Metadata file not found, redirecting to sample video"
            );
            history.push(`${process.env.PUBLIC_URL}/videodemosample`);
            return;
          }

          throw new Error(
            `Failed to load video data (Status: ${response.status})`
          );
        }

        const data = await response.json();
        console.log("Video metadata:", data);

        // Store the metadata in state
        setVideoMetadata(data);
        setLoading(false);
      } catch (err) {
        console.error("Error loading video metadata:", err);

        // If there's any error in fetching or parsing, redirect to sample video
        console.error("Error with metadata, redirecting to sample video");
        history.push(`${process.env.PUBLIC_URL}/videodemosample`);
      }
    };

    fetchVideoData();
  }, [videoId, history]);

  // Sidebar category clicks map onto the section that owns that category.
  // Index 0 is always "Introduction"; anything after it lives in the demo.
  const handleCategoryClick = (index) => {
    if (index === 0) setActiveSection(SECTION_INTRO_ONE);
    else setActiveSection(SECTION_DEMO);
  };

  const handleShowConnection = () => {
    setShowConnectionModal(false);
    setActiveSection(SECTION_DEMO);
  };

  const handleOptionSelected = (option) => {
    console.log(`Selected option: ${option}`);
    setShowPostVideoModal(false);
  };

  const demoVideoUrl =
    videoMetadata?.video_url ||
    "https://elqmq.upcloudobjects.com/demo-videos/mod/v2-sample-group.mp4";

  const handleDownload = () => {
    // Create an invisible anchor element
    const a = document.createElement("a");
    a.href = demoVideoUrl;
    // Extract filename from URL or use a default name
    const filename = demoVideoUrl.split("/").pop() || "demo-video.mp4";
    a.download = filename;
    // Append to body, trigger click, and remove
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Check if loading or error
  if (loading) {
    return (
      <React.Fragment>
        <StartupHeader />
        <div className="main-content">
          <Container>
            <div className="text-center mt-5"></div>
          </Container>
        </div>
      </React.Fragment>
    );
  }

  if (error) {
    return (
      <React.Fragment>
        <StartupHeader />
        <div className="main-content">
          <Container>
            <div className="text-center mt-5">
              <h3>Error: {error}</h3>
            </div>
          </Container>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <StartupHeader />

      <div className="main-content">
        <div className="main-banner" style={{ height: "100vh" }}>
          <div className="dashboard-wrapper resumes-page">
            <Container fluid style={{ maxWidth: "1500px" }}>
              <div className="dashboard-col-bg public-wrapper outline-form">
                <Row className="justify-content-md-center">
                  <Col md={12} className="mx-auto">
                    <h1 className="mb-5 ml-3">
                      JobInfoNetwork
                      <sup style={{ fontSize: "0.45em", marginLeft: "2px" }}>
                        TM
                      </sup>
                    </h1>

                    {/* Intro Video — Part 1 */}
                    {activeSection === SECTION_INTRO_ONE && (
                      <>
                        <VideoPlayer
                          videoUrl={INTRO_ONE_URL}
                          timeStampCategories={[]}
                          useLandscape={true}
                          hideChapters={true}
                          onVideoEnd={() => setActiveSection(SECTION_EMAILS)}
                        />
                        <div className="text-center mt-3 mb-4">
                          <Button
                            variant="success"
                            onClick={handleShowConnection}
                          >
                            See a Member to Member Connection
                          </Button>
                        </div>
                      </>
                    )}

                    {/* Emails */}
                    {activeSection === SECTION_EMAILS && (
                      <div>
                        <EmailTestimonials
                          onComplete={() =>
                            setActiveSection(SECTION_INTRO_TWO)
                          }
                        />
                        <div className="text-center mt-3 mb-4">
                          <Button
                            variant="primary"
                            onClick={() =>
                              setActiveSection(SECTION_INTRO_TWO)
                            }
                          >
                            Skip
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Intro Video — Part 2 */}
                    {activeSection === SECTION_INTRO_TWO && (
                      <>
                        <VideoPlayer
                          videoUrl={INTRO_TWO_URL}
                          timeStampCategories={[]}
                          useLandscape={true}
                          hideChapters={true}
                          onVideoEnd={() => setShowConnectionModal(true)}
                        />
                        <div className="text-center mt-3 mb-4">
                          <Button
                            variant="success"
                            onClick={handleShowConnection}
                          >
                            See a Member to Member Connection
                          </Button>
                        </div>
                      </>
                    )}

                    {/* Main Demo Video */}
                    {activeSection === SECTION_DEMO && (
                      <>
                        <VideoPlayer
                          videoUrl={demoVideoUrl}
                          timeStampCategories={processedDemoParts}
                          useLandscape={false}
                          onCategoryClick={handleCategoryClick}
                          onVideoEnd={() => setShowPostVideoModal(true)}
                        />
                        <div className="text-center mt-4 mb-4">
                          <Button
                            variant="primary"
                            onClick={handleDownload}
                            className="download-btn"
                          >
                            <i className="fa fa-download mr-2"></i>
                            Download Video
                          </Button>
                        </div>
                      </>
                    )}
                  </Col>
                </Row>
              </div>
            </Container>
          </div>
        </div>
      </div>

      {/* Shown after intro part 2 finishes */}
      <MemberConnectionModal
        show={showConnectionModal}
        onHide={() => setShowConnectionModal(false)}
        onShowConnection={handleShowConnection}
      />

      {/* Post Video Modal */}
      <PostVideoModal
        show={showPostVideoModal}
        onHide={() => setShowPostVideoModal(false)}
        onOptionSelected={handleOptionSelected}
      />
    </React.Fragment>
  );
}
